/**
 * FunnelPreview - Comparison Page
 * 
 * Displays all 3 funnel variants for A/B testing comparison.
 * Route: /funnel-preview (temporary for local testing)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UpgradeModalA } from './UpgradeModalA';
import { PricingPageB } from './PricingPageB';
import { UpgradeSheetC } from './UpgradeSheetC';
import { PricingPlan, PRICING } from './shared/constants';
import './FunnelPreview.css';

type FunnelVersion = 'A' | 'B' | 'C' | null;

export function FunnelPreview() {
  const [activeVersion, setActiveVersion] = useState<FunnelVersion>(null);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);

  const handleSelectPlan = (plan: PricingPlan) => {
    setSelectedPlan(plan);
    // In real implementation, this would trigger checkout
    console.log('[FunnelPreview] Plan selected:', plan, PRICING[plan]);
  };

  const handleClose = () => {
    setActiveVersion(null);
  };

  // If Version B (full page) is active, render it directly
  if (activeVersion === 'B') {
    return (
      <div className="funnel-preview-fullpage">
        <button 
          className="funnel-preview-back"
          onClick={() => setActiveVersion(null)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Preview
        </button>
        <PricingPageB onSelectPlan={handleSelectPlan} />
      </div>
    );
  }

  return (
    <div className="funnel-preview">
      {/* Header */}
      <header className="funnel-preview-header">
        <Link to="/settings" className="funnel-preview-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Settings
        </Link>
        <h1 className="funnel-preview-title">Upgrade Funnel Preview</h1>
        <p className="funnel-preview-subtitle">
          Compare 3 conversion-optimized upgrade UI variants for NinjaCash Pro
        </p>
      </header>

      {/* Selected plan indicator */}
      {selectedPlan && (
        <div className="funnel-preview-selected">
          <span className="selected-label">Last selected:</span>
          <span className="selected-plan">{PRICING[selectedPlan].label}</span>
          <button 
            className="selected-clear"
            onClick={() => setSelectedPlan(null)}
          >
            Clear
          </button>
        </div>
      )}

      {/* Version cards */}
      <div className="funnel-preview-grid">
        {/* Version A - Modal */}
        <div className="funnel-version-card">
          <div className="version-header">
            <span className="version-badge">A</span>
            <h2 className="version-title">Modal</h2>
          </div>
          <p className="version-description">
            Clean centered modal overlay. Benefits list with checkmarks, 
            three pricing buttons side-by-side, "No thanks" dismiss link.
          </p>
          <ul className="version-features">
            <li>Quick to see, quick to dismiss</li>
            <li>Works on all screen sizes</li>
            <li>Non-blocking (can close and continue)</li>
            <li>Best for: Sync toggle trigger, second device login</li>
          </ul>
          <div className="version-triggers">
            <span className="trigger-label">Trigger sources:</span>
            <code>sync-toggle</code>
            <code>second-device</code>
            <code>30-day-prompt</code>
          </div>
          <button 
            className="version-preview-btn"
            onClick={() => setActiveVersion('A')}
          >
            Preview Modal
          </button>
        </div>

        {/* Version B - Full Page */}
        <div className="funnel-version-card funnel-version-card--featured">
          <div className="version-header">
            <span className="version-badge version-badge--featured">B</span>
            <h2 className="version-title">Full Page</h2>
            <span className="version-recommended">SEO-ready</span>
          </div>
          <p className="version-description">
            Dedicated pricing page with hero section, comparison table, 
            FAQ section, and footer. Good for SEO and shareable URLs.
          </p>
          <ul className="version-features">
            <li>Shareable URL (/pricing-b)</li>
            <li>SEO-optimized content</li>
            <li>Comprehensive information</li>
            <li>Best for: Marketing links, email campaigns</li>
          </ul>
          <div className="version-triggers">
            <span className="trigger-label">Routes:</span>
            <code>/pricing-b</code>
            <code>/funnel-preview</code>
          </div>
          <button 
            className="version-preview-btn version-preview-btn--featured"
            onClick={() => setActiveVersion('B')}
          >
            Preview Full Page
          </button>
        </div>

        {/* Version C - Bottom Sheet */}
        <div className="funnel-version-card">
          <div className="version-header">
            <span className="version-badge">C</span>
            <h2 className="version-title">Bottom Sheet</h2>
          </div>
          <p className="version-description">
            Mobile-native bottom sheet with drag handle, progressive disclosure, 
            and swipe-down dismiss. Thumb-friendly buttons.
          </p>
          <ul className="version-features">
            <li>Native mobile feel</li>
            <li>Progressive disclosure (expand for more)</li>
            <li>Swipe gesture support</li>
            <li>Best for: Mobile users, quick decisions</li>
          </ul>
          <div className="version-triggers">
            <span className="trigger-label">Gestures:</span>
            <code>swipe-down</code>
            <code>tap-expand</code>
          </div>
          <button 
            className="version-preview-btn"
            onClick={() => setActiveVersion('C')}
          >
            Preview Bottom Sheet
          </button>
        </div>
      </div>

      {/* Implementation notes */}
      <section className="funnel-preview-notes">
        <h2 className="notes-title">Implementation Notes</h2>
        <div className="notes-grid">
          <div className="note-item">
            <h3>Trigger Points</h3>
            <ul>
              <li><strong>Sync Toggle:</strong> Free user clicks "Enable Sync" in Settings</li>
              <li><strong>Second Device:</strong> Free user logs in on another device</li>
              <li><strong>30-Day Prompt:</strong> After 30 days of active free use</li>
            </ul>
          </div>
          <div className="note-item">
            <h3>Pricing</h3>
            <ul>
              <li><strong>Monthly:</strong> $4.99/mo</li>
              <li><strong>Yearly:</strong> $49/yr (save 17%) - Best Value</li>
              <li><strong>Lifetime:</strong> $79 one-time (47 of 100 remaining)</li>
            </ul>
          </div>
          <div className="note-item">
            <h3>Design System</h3>
            <ul>
              <li>Theme: Tokyo Night (mandatory)</li>
              <li>Max sync size: 20MB (database safeguard)</li>
              <li>All CSS uses Tokyo Night variables</li>
            </ul>
          </div>
          <div className="note-item">
            <h3>A/B Testing</h3>
            <ul>
              <li>Track: open rate, conversion rate, revenue</li>
              <li>Segment by: trigger source, device type</li>
              <li>Winner becomes default after sufficient data</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Modal A */}
      <UpgradeModalA
        isOpen={activeVersion === 'A'}
        onClose={handleClose}
        onSelectPlan={handleSelectPlan}
        triggerSource="sync-toggle"
      />

      {/* Sheet C */}
      <UpgradeSheetC
        isOpen={activeVersion === 'C'}
        onClose={handleClose}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}
